/* eslint-disable tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value, react/no-array-index-key */
import classNames from 'classnames';
import { Fragment } from 'react';
import { MaterialIcon } from '../../components/MaterialIcon';
import { RawHtml } from '../../components/RawHtml/RawHtml';
import type { PurchaseSuccessStepperProps } from './types';

/**
 * Purchase Success progress stepper (CMS-1794) - the fixed, all-complete
 * journey indicator at the top of the TI confirmation page. Reuses the checkout
 * StepIcons visual (circles + connectors), but decoupled from `CheckoutPageStep`
 * / `goToPage`: every prior step is a completed check and the final step is the
 * "covered" shield in its active state (per Figma 26102:22166, the stepper is
 * identical in the success and pending states - so this component carries no
 * data/state; the error state simply hides it via the page controller).
 *
 * Fully presentational and non-interactive (no back-navigation, unlike
 * checkout). Icons go through `MaterialIcon` (`check` / `verified_user` are both
 * whitelisted).
 */
const DEFAULT_COMPLETED_STEPS = 4;

const CIRCLE_BASE =
  'flex size-8 shrink-0 items-center justify-center rounded-full border md:size-12';
const CONNECTOR = 'mx-4 w-full border-fill/primary';

const CompletedCircle = (): JSX.Element => (
  <span
    data-component="purchase-success-stepper-step"
    className={classNames(CIRCLE_BASE, 'border-brand-primary bg-fill/primary-light')}
  >
    <MaterialIcon icon="check" px={22} className="text-brand-primary" />
  </span>
);

export const PurchaseSuccessStepper = ({
  completedSteps = DEFAULT_COMPLETED_STEPS,
  greetingHtml,
}: PurchaseSuccessStepperProps): JSX.Element => {
  const steps = Math.max(0, completedSteps);

  return (
    <>
      <div
        data-component="purchase-success-stepper"
        className="flex items-center justify-between"
      >
        {Array.from({ length: steps }).map((_, index) => (
          <Fragment key={index}>
            <CompletedCircle />
            <hr aria-hidden className={CONNECTOR} />
          </Fragment>
        ))}

        {/* Final "you are now covered" step - reached/active on this page. */}
        <span
          data-component="purchase-success-stepper-covered"
          className={classNames(CIRCLE_BASE, 'border-0 bg-brand-primary')}
        >
          <MaterialIcon icon="verified_user" px={22} className="text-white" />
        </span>
      </div>

      {/* Editor-authored greeting ("Congrats {First Name}, you are now
          covered!"), state/provider-keyed, tokens pre-interpolated by the app
          wrapper. Rendered BELOW the stepper per Figma (25545-54819: stepper on
          top, greeting under it). Absent on SSR / until the purchase resolves. */}
      {greetingHtml && (
        <div
          data-component="purchase-success-stepper-greeting"
          className="mt-6"
        >
          <RawHtml content={greetingHtml} />
        </div>
      )}
    </>
  );
};
